import React from "react";
import { format } from "date-fns";
import { CARENCE_EMPLOYEUR, CARENCE_SECU, simulate } from "./ijRules";

const SENIORITY = [
    { value: 0, label: "Moins d'1 an" },
    { value: 1, label: "1 à 5 ans" },
    { value: 6, label: "6 à 10 ans" },
    { value: 11, label: "11 à 15 ans" },
    { value: 16, label: "16 à 20 ans" },
    { value: 21, label: "21 à 25 ans" },
    { value: 26, label: "26 à 30 ans" },
    { value: 31, label: "31 ans et plus" },
];

const euros = (n) => n.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
const num = (s) => parseFloat(String(s).replace(",", "."));
const inputCls = "py-2 w-28 text-center px-2 font-bold rounded-lg text-base sm:text-lg focus:outline-none focus:ring focus:ring-slate-300";

function Row({ label, value, sub }) {
    return (
        <div className="flex items-center justify-between bg-stone-100 rounded-lg px-4 py-2">
            <div className="font-semibold text-sm sm:text-base text-gray-900">{label}</div>
            <div className="text-right shrink-0 ml-2">
                <div className="font-bold text-base sm:text-lg text-emerald-800">{value}</div>
                {sub && <div className="text-xs text-gray-600">{sub}</div>}
            </div>
        </div>
    );
}

// "Jours 1 à 3 : …" segments for the timeline.
function timeline(r, days, prolongation) {
    const segs = [];
    const cs = prolongation ? 0 : CARENCE_SECU;
    const ce = prolongation ? 0 : CARENCE_EMPLOYEUR;
    const push = (from, to, text) => { if (to >= from) segs.push({ from, to, text }); };
    push(1, Math.min(cs, days), "délai de carence : aucune indemnité");
    const employerStart = r.period > 0 ? ce + 1 : days + 1;
    push(cs + 1, Math.min(employerStart - 1, days), "indemnités journalières seules");
    if (r.period > 0) {
        push(ce + 1, Math.min(ce + r.period, days), "maintien à 90 % du salaire brut (IJ + employeur)");
        push(ce + r.period + 1, Math.min(ce + 2 * r.period, days), "maintien à 66,66 % du salaire brut (IJ + employeur)");
        push(ce + 2 * r.period + 1, days, "indemnités journalières seules");
    }
    return segs;
}

export default function ArretMaladie() {
    const [salary, setSalary] = React.useState("");
    const [start, setStart] = React.useState(format(new Date(), "yyyy-MM-dd"));
    const [days, setDays] = React.useState("10");
    const [seniority, setSeniority] = React.useState(0);
    const [prolongation, setProlongation] = React.useState(false);

    const d = parseInt(days, 10);
    const tooEarly = start && start < "2025-04-01";
    const r = !tooEarly ? simulate({ monthlyGross: num(salary), dateIso: start, days: d, seniorityYears: seniority, prolongation }) : null;

    return (
        <>
            <div className="p-4 my-2 sm:my-4 bg-stone-100 rounded-lg flex flex-col gap-4">
                <label className="flex flex-col gap-1">
                    <span className="text-sm sm:text-base">Salaire brut mensuel moyen <span className="text-gray-500">(3 derniers mois)</span></span>
                    <span className="flex items-center gap-2">
                        <input type="text" inputMode="decimal" placeholder="—" className={inputCls} value={salary}
                            onChange={(e) => setSalary(e.target.value.replace(/[^0-9.,]/g, ""))} />
                        <span className="text-base sm:text-xl font-bold">€</span>
                    </span>
                </label>
                <div className="flex flex-wrap gap-6">
                    <label className="flex flex-col gap-1">
                        <span className="text-sm sm:text-base">Début de l'arrêt</span>
                        <input type="date" className="dropdown m-0 min-w-32" value={start} min="2025-04-01" max="2035-12-31"
                            onChange={(e) => setStart(e.target.value)} />
                    </label>
                    <label className="flex flex-col gap-1">
                        <span className="text-sm sm:text-base">Durée</span>
                        <span className="flex items-center gap-2">
                            <input type="text" inputMode="numeric" className={`${inputCls} w-20`} value={days}
                                onChange={(e) => setDays(e.target.value.replace(/[^0-9]/g, "").slice(0, 3))} />
                            <span className="text-base sm:text-xl font-bold">jours</span>
                        </span>
                    </label>
                </div>
                <label className="flex flex-col gap-1">
                    <span className="text-sm sm:text-base">Ancienneté dans l'entreprise</span>
                    <select className="dropdown-filter select" value={seniority} onChange={(e) => setSeniority(Number(e.target.value))}>
                        {SENIORITY.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                </label>
                <label className="flex items-start gap-2 text-sm">
                    <input type="checkbox" className="mt-1" checked={prolongation} onChange={(e) => setProlongation(e.target.checked)} />
                    <span>Prolongation d'un arrêt en cours <span className="text-gray-500">(pas de nouveau délai de carence)</span></span>
                </label>
            </div>

            {tooEarly && <p className="text-sm text-red-700 text-center mb-2">Le calculateur applique les règles en vigueur pour les arrêts débutant depuis le 1er avril 2025.</p>}

            {!r ? (
                <div className="p-6 my-2 sm:my-4 bg-stone-50 border border-dashed border-stone-300 rounded-lg text-center">
                    <p className="text-gray-900 font-semibold text-sm sm:text-base">Renseignez votre salaire brut et la durée de l'arrêt</p>
                    <p className="text-gray-600 text-sm mt-2">Vous obtiendrez vos indemnités journalières brutes et nettes, le complément de votre employeur et votre perte de salaire estimée.</p>
                </div>
            ) : (
                <>
                    <div className="p-4 my-2 sm:my-4 rounded-lg w-full bg-gradient-to-b from-emerald-900 to-emerald-700 text-white text-center">
                        <div className="text-sm text-emerald-200">Indemnité journalière de la Sécurité sociale</div>
                        <div className="text-4xl sm:text-5xl font-bold my-1">{euros(r.ij.nette)} <span className="text-base font-semibold">net / jour</span></div>
                        <div className="text-sm">soit {euros(r.ij.brute)} brut par jour</div>
                        {r.ij.capped && <div className="text-xs text-emerald-100 mt-1">Salaire plafonné à {euros(r.ij.cap)} par mois (1,4 SMIC) pour ce calcul.</div>}
                    </div>

                    <div className="mb-2 font-bold text-center w-full sm:text-lg cursor-default">Sur {d} jour{d > 1 ? "s" : ""} d'arrêt</div>
                    <div className="flex flex-col gap-2">
                        <Row label="Jours indemnisés" value={`${r.ijDays} jour${r.ijDays > 1 ? "s" : ""}`} sub={prolongation ? "pas de carence" : `après ${CARENCE_SECU} jours de carence`} />
                        <Row label="Indemnités journalières" value={euros(r.ijNetteTotal)} sub={`net · ${euros(r.ijBruteTotal)} brut`} />
                        {r.period > 0 && <Row label="Complément employeur" value={euros(r.complementTotal)} sub="brut, maintien de salaire légal" />}
                        <Row label="Salaire brut habituel" value={euros(r.salaryTotal)} sub="sur la même période" />
                        <Row label="Perte de revenu estimée" value={euros(r.lossGross)} sub="en brut, avant cotisations" />
                    </div>

                    <div className="p-3 my-2 sm:my-4 bg-stone-100 rounded-lg text-sm">
                        <p className="font-semibold text-gray-900 mb-1">Déroulé de l'indemnisation</p>
                        <ul className="list-disc pl-4 text-gray-700">
                            {timeline(r, d, prolongation).map((s) => (
                                <li key={s.from}>{s.from === s.to ? `Jour ${s.from}` : `Jours ${s.from} à ${s.to}`} : {s.text}</li>
                            ))}
                        </ul>
                        {seniority === 0 && <p className="text-xs text-gray-600 mt-2">Avec moins d'1 an d'ancienneté, l'employeur n'a pas d'obligation légale de complément ; votre convention collective peut en prévoir un.</p>}
                    </div>
                    <p className="text-xs text-gray-600">
                        Estimation pour un salarié du secteur privé, arrêt maladie non professionnel, selon les règles légales. Votre convention collective peut prévoir un maintien de salaire plus favorable (sans carence, à 100 %). Le complément employeur est calculé sur une base de 30 jours par mois ; les indemnités déjà versées au cours des 12 derniers mois réduisent les durées de maintien.
                    </p>
                </>
            )}
        </>
    );
}
